import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { getImageProps } from '../../utils/imageUtils';
import { useCustomTheme } from '../../contexts/ThemeContext';

const HeroSlider = ({ sliderImages = [], restaurant = {} }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // Auto-play functionality only
  useEffect(() => {
    if (sliderImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextSlide = (prev + 1) % sliderImages.length;
        return nextSlide;
      });
    }, 5000); 

    return () => {
      clearInterval(interval);
    };
  }, [sliderImages.length]);

  // Preload images for better performance
  useEffect(() => {
    if (sliderImages && sliderImages.length > 0) {
      // Reset current slide when images change
      setCurrentSlide(0);

      const preloadImages = () => {
        const imagePromises = sliderImages.map((image) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load image: ${image.image_url}`));
            img.src = image.image_url;
          });
        });

        Promise.all(imagePromises)
          .then(() => {
            // console.log('All slider images preloaded successfully');
            setImagesLoaded(true);
          })
          .catch((error) => {
            // console.error('Error preloading images:', error);
            setImagesLoaded(true); // Still show slider even if some images fail
          });
      };

      preloadImages();
    } else {
      setImagesLoaded(true);
    }
  }, [sliderImages]);

  // Show loading state while images are being preloaded
  if (!imagesLoaded) {
    return (
      <Box
        sx={{
          marginTop: '0px !important',
          position: 'relative',
          minHeight: { xs: '20vh', md: '70vh' },
          maxHeight: { xs: '20vh', md: '70vh' },
          mt: { xs: 7, md: 8 },
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
            : 'linear-gradient(135deg, #2d1b14 0%, #8B4513 50%, #2d1b14 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white', opacity: 0.8 }}>
            Loading images...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!sliderImages || sliderImages.length === 0) {
    // Fallback to static hero section if no slider images
    return (
      <Box
        sx={{
          marginTop: '0px !important',
          position: 'relative',
          // minHeight: { xs: '20vh', sm: '60vh', md: '70vh' },
          // maxHeight: { xs: '20vh', sm: '60vh', md: '70vh' },
          mt: { xs: 7, sm: 7, md: 8 },
          background: isDarkMode
            ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)'
            : 'linear-gradient(135deg, #2d1b14 0%, #8B4513 50%, #2d1b14 100%)',
          color: 'white',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Container maxWidth="lg" sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
        }}>
          <Box sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            // minHeight: '100%',
          }}>
            {/* Banner only - no text content */}
          </Box>
        </Container>
      </Box>
    );
  }

  // Ensure currentSlide is within bounds
  const safeCurrentSlide = Math.max(0, Math.min(currentSlide, sliderImages.length - 1));
  const currentImage = sliderImages[safeCurrentSlide];

  return (
    <Box
      sx={{
        marginTop: '0px !important',
        position: 'relative',
        minHeight: { xs: '10vh', sm: '35vh', md: '60vh',lg:'70vh' },
        maxHeight: { xs: '10vh', sm: '35vh', md: '60vh',lg:'70vh' },
        mt: { xs: 7, md: 8 },
        overflow: 'hidden',
        width: '100%',
        cursor: 'default',
        "&:hover": {
          cursor: 'default'
        }
      }}
    >
      {/* Single Image Display */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${currentImage.image_url})`,
          backgroundSize: { xs: 'cover', sm: 'cover', md: 'cover' },
          backgroundPosition: { xs: 'center center', sm: 'center center', md: 'center center' },
          backgroundRepeat: 'no-repeat',
          transition: 'opacity 0.5s ease-in-out',
          width: '100%',
          height: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            zIndex: 1,
          }
        }}
      />

      {/* No overlay - images at normal brightness */}

      {/* Content - Perfectly Centered */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 3,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '100%',
        }}>
          <Box sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}>
            {/* Banner only - no text content */}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HeroSlider;